import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useAuth0 } from '@auth0/auth0-react';
import AppModal from '../AppModal';
import {
  getMemberRecordOriginalBlob,
  getMemberRecordVectorDocument,
} from '../../services/cohort-chat.service';
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeReadableString = (value) => value
  .replace(/\r\n/g, '\n')
  .replace(/\\r\\n/g, '\n')
  .replace(/\\n/g, '\n')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const extractedTextFromPayload = (payload) => {
  const value = payload?.extracted_text;
  if (value === null || value === undefined) return '';
  return normalizeReadableString(String(value));
};

const countMatches = (text, query) => {
  if (!query.trim()) return 0;
  const matches = text.match(new RegExp(escapeRegExp(query.trim()), 'gi'));
  return matches ? matches.length : 0;
};

const renderHighlightedText = ({
  text,
  query,
  activeMatchIndex,
  nextMatchIndex,
}) => {
  if (!query.trim()) return text;
  const trimmedQuery = query.trim();
  const regex = new RegExp(`(${escapeRegExp(trimmedQuery)})`, 'gi');
  return text.split(regex).map((part, index) => (
    part.toLowerCase() === trimmedQuery.toLowerCase()
      ? (
        <mark
          key={`${part}-${index}`}
          className={nextMatchIndex.current === activeMatchIndex ? 'record-citation-match-active' : undefined}
          data-record-citation-match={nextMatchIndex.current++}
        >
          {part}
        </mark>
      )
      : part
  ));
};

const RecordCitationDrawer = ({
  open,
  onClose,
  citation,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const jsonContainerRef = useRef(null);
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [originalOpen, setOriginalOpen] = useState(false);
  const [originalUrl, setOriginalUrl] = useState('');
  const [originalType, setOriginalType] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [originalError, setOriginalError] = useState('');
  const [originalLoading, setOriginalLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadDocument = async () => {
      if (!open || !citation?.memberId || !citation?.docId) {
        setDocumentData(null);
        return;
      }

      setLoading(true);
      setError('');
      setSearch('');
      setActiveMatchIndex(0);
      setOriginalOpen(false);
      setOriginalError('');

      try {
        const token = await getAccessTokenSilently();
        const { data, error: apiError } = await getMemberRecordVectorDocument(token, {
          memberId: citation.memberId,
          docId: citation.docId,
          datasetName: citation.datasetName,
        });
        if (cancelled) return;
        if (apiError) {
          const message = typeof apiError === 'string' ? apiError : apiError.message;
          setError(message || 'Failed to load record document');
          setDocumentData(null);
          return;
        }
        setDocumentData(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load record document');
          setDocumentData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDocument();
    return () => {
      cancelled = true;
    };
  }, [citation, getAccessTokenSilently, open]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    const loadOriginal = async () => {
      if (!originalOpen || !open || !citation?.memberId || !citation?.docId) {
        setOriginalUrl('');
        setOriginalText('');
        return;
      }
      setOriginalLoading(true);
      setOriginalError('');
      try {
        const token = await getAccessTokenSilently();
        const { blob, contentType, error: apiError } = await getMemberRecordOriginalBlob(token, {
          memberId: citation.memberId,
          docId: citation.docId,
          datasetName: citation.datasetName,
        });
        if (cancelled) return;
        if (apiError || !blob) {
          setOriginalError(apiError || 'Original record is unavailable');
          setOriginalUrl('');
          setOriginalText('');
          return;
        }
        const type = contentType || blob.type || '';
        objectUrl = URL.createObjectURL(blob);
        setOriginalType(type);
        setOriginalUrl(objectUrl);
        setOriginalText(type.startsWith('text/plain') ? await blob.text() : '');
      } catch (err) {
        if (!cancelled) setOriginalError(err.message || 'Failed to load original record');
      } finally {
        if (!cancelled) setOriginalLoading(false);
      }
    };
    loadOriginal();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [citation, getAccessTokenSilently, open, originalOpen]);

  const extractedText = useMemo(() => (
    documentData ? extractedTextFromPayload(documentData.payload) : ''
  ), [documentData]);
  const matchCount = useMemo(() => countMatches(extractedText, search), [extractedText, search]);
  const currentMatchLabel = matchCount > 0 ? `${activeMatchIndex + 1} / ${matchCount} matches` : '0 matches';

  useEffect(() => {
    if (!search.trim() || matchCount === 0) {
      setActiveMatchIndex(0);
      return;
    }
    if (activeMatchIndex >= matchCount) {
      setActiveMatchIndex(0);
    }
  }, [activeMatchIndex, matchCount, search]);

  useEffect(() => {
    if (!search.trim() || matchCount === 0) return;
    const activeMatch = jsonContainerRef.current?.querySelector(
      `mark[data-record-citation-match="${activeMatchIndex}"]`
    );
    activeMatch?.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [activeMatchIndex, matchCount, search]);

  const goToNextMatch = useCallback(() => {
    if (matchCount === 0) return;
    setActiveMatchIndex((current) => (current + 1) % matchCount);
  }, [matchCount]);

  const goToPreviousMatch = useCallback(() => {
    if (matchCount === 0) return;
    setActiveMatchIndex((current) => (current - 1 + matchCount) % matchCount);
  }, [matchCount]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setActiveMatchIndex(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      goToNextMatch();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      goToPreviousMatch();
    }
  };

  const nextMatchIndex = { current: 0 };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ disableEnforceFocus: originalOpen }}
      PaperProps={{ className: 'record-citation-drawer-paper' }}
    >
      <Box className="record-citation-drawer-content">
        <Box className="record-citation-drawer-header">
          <Box>
            <Typography variant="h6" className="record-citation-drawer-title">
              Record Citation
            </Typography>
            {citation?.docId && (
              <Typography variant="body2" color="text.secondary">
                {citation.docId}.json
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close record citation drawer">
            <CloseIcon />
          </IconButton>
        </Box>

        {documentData && (
          <Box className="record-citation-metadata-row">
            <Chip size="small" label={`Member ${documentData.member_id}`} />
            {documentData.record_type && <Chip size="small" label={documentData.record_type} />}
            {documentData.record_date && <Chip size="small" label={documentData.record_date} />}
            {citation?.chunkIndex !== undefined && <Chip size="small" label={`Chunk ${citation.chunkIndex}`} />}
          </Box>
        )}

        <Divider />

        <Box className="record-citation-view-toggle">
          <Button
            size="small"
            variant="contained"
          >
            Text
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setOriginalOpen(true)}
          >
            Original
          </Button>
        </Box>

        <>
            <Box className="record-citation-search-row">
              <TextField
                className="record-citation-search-input"
                size="small"
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search extracted text"
                InputProps={{ startAdornment: <SearchIcon fontSize="small" className="record-citation-search-icon" /> }}
                helperText="Use up/down arrow keys to move between matches."
              />
              {search && (
                <Box className="record-citation-match-controls">
                  <Typography variant="caption" color="text.secondary">
                    {currentMatchLabel}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={goToPreviousMatch}
                    disabled={matchCount === 0}
                    startIcon={<KeyboardArrowUpIcon />}
                  >
                    Prev
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={goToNextMatch}
                    disabled={matchCount === 0}
                    startIcon={<KeyboardArrowDownIcon />}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Box>

            {loading && (
              <Box className="record-citation-loading-row">
                <CircularProgress size={18} />
                <Typography variant="body2">Loading record document...</Typography>
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {documentData && (
              <Box
                ref={jsonContainerRef}
                className="record-citation-json"
              >
                {extractedText ? (
                  <Box component="pre" className="record-citation-extracted-text">
                    {renderHighlightedText({
                      text: extractedText,
                      query: search,
                      activeMatchIndex,
                      nextMatchIndex,
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No extracted_text found for this record.
                  </Typography>
                )}
              </Box>
            )}
        </>
      </Box>
      <AppModal
        open={originalOpen}
        onClose={() => setOriginalOpen(false)}
        title={citation?.docId ? `${citation.docId}.json` : 'Original record'}
        maxWidth="xl"
        className="record-citation-original-modal"
      >
        <Box className="record-citation-original">
          {originalLoading ? (
            <Box className="record-citation-loading-row">
              <CircularProgress size={18} />
              <Typography variant="body2">Loading original record...</Typography>
            </Box>
          ) : null}
          {originalError ? <Alert severity="error">{originalError}</Alert> : null}
          {originalText ? (
            <Box component="pre" className="record-citation-original-text">{originalText}</Box>
          ) : null}
          {originalUrl && !originalText && originalType.startsWith('image/') ? (
            <img className="record-citation-original-frame" src={originalUrl} alt="Original record" />
          ) : null}
          {originalUrl && !originalText && originalType.startsWith('application/pdf') ? (
            <embed
              className="record-citation-original-frame"
              title="Original record"
              src={originalUrl}
              type="application/pdf"
            />
          ) : null}
          {originalUrl && !originalText && !originalType.startsWith('image/') && !originalType.startsWith('application/pdf') ? (
            <iframe
              className="record-citation-original-frame"
              title="Original record"
              src={originalUrl}
              sandbox={originalType.startsWith('text/html') ? '' : undefined}
            />
          ) : null}
        </Box>
      </AppModal>
    </Drawer>
  );
};

export default RecordCitationDrawer;
